import { useRef, useState } from 'react';
import { useGameContext } from '@/state/GameContext';
import type { ItemId } from '@/types/domain';
import { RARITY_COLORS } from '@/state/settings';

interface Props {
  itemId: ItemId;
  size?: number;
  count?: number;
  favorite?: boolean;
  draggable?: boolean;
  onDragStart?: (id: ItemId) => void;
  onClick?: (id: ItemId) => void;
  showTooltip?: boolean;
}

export function ItemIcon({ itemId, size = 40, count, favorite, draggable, onDragStart, onClick, showTooltip = true }: Props) {
  const game = useGameContext();
  const meta = game.metaFor(itemId);
  const url = game.urlFor(itemId);
  const [pos, setPos] = useState<{ x: number; y: number } | null>(null);
  const ref = useRef<HTMLDivElement>(null);

  const name = meta?.name || itemId;
  const isCustom = itemId.startsWith('custom:');

  return (
    <div
      ref={ref}
      className={`item-cell${favorite ? ' fav' : ''}`}
      style={{ width: size, height: size }}
      draggable={draggable}
      onDragStart={(e) => {
        if (!draggable) return;
        e.dataTransfer.setData('text/plain', itemId);
        e.dataTransfer.effectAllowed = 'copy';
        onDragStart?.(itemId);
      }}
      onClick={() => onClick?.(itemId)}
      onMouseEnter={(e) => showTooltip && setPos({ x: e.clientX, y: e.clientY })}
      onMouseMove={(e) => showTooltip && setPos({ x: e.clientX, y: e.clientY })}
      onMouseLeave={() => setPos(null)}
      title={showTooltip ? undefined : name}
    >
      {url ? <img src={url} alt={name} draggable={false} /> : <span style={{ fontSize: 10 }}>{name.slice(0, 3)}</span>}
      {count && count > 1 ? <span className="count">{count}</span> : null}
      {pos && showTooltip && (
        <div className="tooltip" style={{ left: pos.x + 14, top: pos.y + 14 }}>
          <div className="t-name" style={{ color: meta ? RARITY_COLORS[meta.rarity] || '#fff' : '#fff' }}>
            {name}
            {isCustom ? <span className="badge custom" style={{ marginLeft: 6 }}>custom</span> : null}
          </div>
          {meta?.purpose ? <div className="muted">{meta.purpose}</div> : null}
          {meta ? (
            <div className="muted">
              {meta.category} &middot; tier {meta.tier} &middot; {meta.rarity}
            </div>
          ) : null}
          {meta?.tags?.length ? <div className="t-tags">#{meta.tags.slice(0, 6).join(' #')}</div> : null}
          <div className="t-id">{itemId}</div>
        </div>
      )}
    </div>
  );
}
